import {
    BudgetExpenseOrIncome,
    BudgetPhases,
    BudgetRealOrOrder,
    BudgetSections,
} from 'business/constants';
import { translateBlockValue } from 'i18n';
import { getFieldForPhase } from '../shared/phases/helpers';
import {
    BudgetExcelExportCol,
    ExportOptions,
    ExtraColumn,
    prepareInscriptionDataForExport,
} from './export';
import {
    getFieldValueFromPhase,
    InscriptionT,
} from '../shared/entities/inscription';
import {
    INSCRIPTION_EXPENSE_INCOME,
    PhaseFieldMapping,
    REAL_ORDER_FIELD,
    SECTION_FIELD,
} from '../config/constants';
import { exportToExcel } from 'helpers/exportHelpers';
import { trackEventBudget } from 'helpers/trackerHelpers';
import { SecondaryBudgetAxisSources } from 'routes/Support/components/ClientSupport/components/BudgetConfiguration/types';
import { StatusesSettingsT } from 'routes/BudgetModule/config/types';
import { TFunction } from 'i18next';
import { Dispatch } from 'store/store';

export const groupInscriptionsByImputations = (
    inscriptionData: Record<string, ResponseDataValueT>[],
    phase: BudgetPhases,
) => {
    const amountField = getFieldForPhase(phase, 'amount');
    const commentField = getFieldForPhase(phase, 'comment');
    const statusField = 'status';
    return Object.values(
        inscriptionData.reduce(
            (acc: Record<string, Record<string, ResponseDataValueT>>, item) => {
                const {
                    [amountField]: amount,
                    [commentField!]: comment,
                    [statusField]: status,
                    ...rest
                } = item;
                const key = JSON.stringify(rest);
                if (!acc[key]) {
                    acc[key] = {
                        ...item,
                        [amountField]: 0,
                        [commentField!]: '',
                        [statusField]: '',
                    };
                }
                // @ts-expect-error [TS migration] ResponseDataValueT should be generic<T>
                acc[key][amountField] += amount;
                acc[key][commentField!] += acc[key][commentField!]
                    ? `\n${comment}`
                    : `${comment}`;
                acc[key][statusField] += acc[key][statusField]
                    ? `\n${translateBlockValue(String(status))}`
                    : `${translateBlockValue(String(status))}`;
                return acc;
            },
            {},
        ),
    );
};

export const getPhaseSpecificColumns = (
    phase: BudgetPhases,
    fields: ('amount' | 'comment')[],
): ExtraColumn[] => {
    return PhaseFieldMapping.filter((p) => p.phase === phase).flatMap((p) =>
        fields.map((field) => ({
            label: undefined,
            field,
            key: `${p.key}.${field}`,
            getValue: (data: InscriptionT) =>
                (getFieldValueFromPhase(p.phase, field, data) as
                    | string
                    | number) ?? '',
            type: 'text',
        })),
    );
};

/**
 * Check if the label is undefined. Empty string is not undefined, nor is null. We want
 * to be able to have an empty column on the Excel export, so we can't just check for
 * falsy values.
 */
export const isLabelUndefined = (label: string | undefined) =>
    label === undefined;

const FILE_NAMES = {
    operatingRealExpense: 'manty-dépenses-réelles-fonctionnement',
    operatingRealIncome: 'manty-recettes-réelles-fonctionnement',
    operatingOrderExpense: 'manty-dépenses-ordres-fonctionnement',
    operatingOrderIncome: 'manty-recettes-ordres-fonctionnement',
    investmentRealExpense: 'manty-dépenses-réelles-investissement',
    investmentRealIncome: 'manty-recettes-réelles-investissement',
    investmentOrderExpense: 'manty-dépenses-ordres-investissement',
    investmentOrderIncome: 'manty-recettes-ordres-investissement',
};

type GrandAngleFileNames = keyof typeof FILE_NAMES;

const DATA_TO_FILE_SLUG: Readonly<
    Record<
        BudgetSections,
        Record<
            BudgetRealOrOrder,
            Record<BudgetExpenseOrIncome, GrandAngleFileNames>
        >
    >
> = {
    [BudgetSections.OPERATING]: {
        [BudgetRealOrOrder.REAL]: {
            [BudgetExpenseOrIncome.EXPENSE]: 'operatingRealExpense',
            [BudgetExpenseOrIncome.INCOME]: 'operatingRealIncome',
        },
        [BudgetRealOrOrder.ORDER]: {
            [BudgetExpenseOrIncome.EXPENSE]: 'operatingOrderExpense',
            [BudgetExpenseOrIncome.INCOME]: 'operatingOrderIncome',
        },
    },
    [BudgetSections.INVESTMENT]: {
        [BudgetRealOrOrder.REAL]: {
            [BudgetExpenseOrIncome.EXPENSE]: 'investmentRealExpense',
            [BudgetExpenseOrIncome.INCOME]: 'investmentRealIncome',
        },
        [BudgetRealOrOrder.ORDER]: {
            [BudgetExpenseOrIncome.EXPENSE]: 'investmentOrderExpense',
            [BudgetExpenseOrIncome.INCOME]: 'investmentOrderIncome',
        },
    },
};

type GrandAngleInscriptionsByFile = Record<GrandAngleFileNames, InscriptionT[]>;

const getInscriptionsByFileForGrandAngle = (inscriptions: InscriptionT[]) => {
    return inscriptions?.reduce(
        (acc: GrandAngleInscriptionsByFile, inscription: InscriptionT) => {
            const section = inscription[SECTION_FIELD];
            const realOrder = inscription[REAL_ORDER_FIELD];
            const expenseIncome = inscription[
                INSCRIPTION_EXPENSE_INCOME
            ] as BudgetExpenseOrIncome;
            if (!section || !realOrder || !expenseIncome) return acc;
            const fileSlug =
                DATA_TO_FILE_SLUG[section]?.[realOrder]?.[expenseIncome];
            acc[fileSlug].push(inscription);
            return acc;
        },
        {
            operatingRealExpense: [],
            operatingRealIncome: [],
            operatingOrderExpense: [],
            operatingOrderIncome: [],
            investmentRealExpense: [],
            investmentRealIncome: [],
            investmentOrderExpense: [],
            investmentOrderIncome: [],
        },
    );
};

type ExcelExportParameters = {
    inscriptionsData: { columns: BudgetExcelExportCol[]; data: InscriptionT[] };
    phase: BudgetPhases;
    statusesSettings: StatusesSettingsT;
    softwares: Set<string>;
    exportOptions: ExportOptions;
    trackEventName?: string;
    trackingMetadata?: {
        budgetProposal: string;
        phase: string;
    };
    t: TFunction;
    dispatch: Dispatch;
    isReimport?: boolean;
    secondaryBudgetAxisSourceTable?: SecondaryBudgetAxisSources | null;
};
export const getMultipleFileExcelExport = ({
    inscriptionsData,
    phase,
    statusesSettings,
    softwares,
    exportOptions,
    trackEventName,
    trackingMetadata,
    t,
    dispatch,
    isReimport = false,
    secondaryBudgetAxisSourceTable,
}: ExcelExportParameters) => {
    const inscriptionsGroupedByExportedFile =
        getInscriptionsByFileForGrandAngle(inscriptionsData.data);
    const preparedDataByFile = Object.entries(
        inscriptionsGroupedByExportedFile,
    ).reduce(
        (
            acc: {
                selectedColsToExport: BudgetExcelExportCol[];
                selectedDataToExport: InscriptionT[];
                fileName: string;
            }[],
            [fileSlug, inscriptions]: [GrandAngleFileNames, InscriptionT[]],
        ) => {
            const { selectedColsToExport, selectedDataToExport } =
                prepareInscriptionDataForExport({
                    inscriptionsDataColumns: inscriptionsData.columns,
                    inscriptionsData: inscriptions,
                    phase,
                    statusesSettings,
                    exportOptions,
                    t,
                    isReimport,
                    secondaryBudgetAxisSourceTable,
                });
            if (selectedDataToExport.length > 0) {
                acc.push({
                    selectedColsToExport,
                    selectedDataToExport,
                    fileName: FILE_NAMES[fileSlug],
                });
            }
            return acc;
        },
        [],
    );

    return () => {
        if (preparedDataByFile.length === 0) {
            dispatch.vision.notifyUser({
                message: t('budgetProposal.noInscriptionToExport'),
                snackType: 'warning',
            });
        } else {
            preparedDataByFile.forEach((exportData) => {
                exportToExcel({
                    selectedColsToExport: exportData.selectedColsToExport,
                    selectedDataToExport: exportData.selectedDataToExport,
                    filename: exportData.fileName,
                    sheetName: exportOptions.sheetName,
                    extraSheets: exportOptions.extraSheets,
                    extraRows: exportOptions.extraRows,
                    softwares,
                    fileFormat: exportOptions.fileFormat,
                    // this function is only used for Grand Angle for now (and the foreseeable future), so we don't need
                    // to check for software
                    origin: 'A6',
                });
            });
        }
        if (trackEventName) {
            trackEventBudget(trackEventName, trackingMetadata);
        }
    };
};

export const getSingleFileExcelExport = ({
    inscriptionsData,
    phase,
    statusesSettings,
    softwares,
    exportOptions,
    trackEventName,
    trackingMetadata,
    t,
    dispatch,
    isReimport = false,
    secondaryBudgetAxisSourceTable,
}: ExcelExportParameters) => {
    const { selectedColsToExport, selectedDataToExport } =
        prepareInscriptionDataForExport({
            inscriptionsDataColumns: inscriptionsData.columns,
            inscriptionsData: inscriptionsData.data,
            phase,
            statusesSettings,
            exportOptions,
            t,
            isReimport,
            secondaryBudgetAxisSourceTable,
        });
    return () => {
        if (
            selectedDataToExport.length === 0 ||
            selectedDataToExport[0] === undefined
        ) {
            dispatch.vision.notifyUser({
                message: t('budgetProposal.noInscriptionToExport'),
                snackType: 'warning',
            });
        } else {
            const origin =
                isReimport && softwares.has('GRAND_ANGLE') ? 'A6' : 'A2';
            exportToExcel({
                selectedColsToExport,
                selectedDataToExport,
                filename: t('budgetProposal.titleCSVSpecialFormat'),
                sheetName: exportOptions.sheetName,
                extraSheets: exportOptions.extraSheets,
                extraRows: exportOptions.extraRows,
                softwares,
                origin,
                fileFormat: exportOptions.fileFormat,
            });
        }
        if (trackEventName) {
            trackEventBudget(trackEventName, trackingMetadata);
        }
    };
};
